import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CmsBannerCarouselComponent, CmsBannerComponent, CmsComponent, CmsService } from '@spartacus/core';
import { CmsComponentData } from '@spartacus/storefront';
import { BehaviorSubject, Observable, Subscription, combineLatest, map, switchMap } from 'rxjs';
import { ToolsService } from '../../../../../../core/services/tools.service';
import { mediaConfig } from '../../../default-media.config';
import { CustomCarousel } from './carousel.model';
import { Banner } from '../../../../../../core/models/banners.model';
import { DEVICE_SIZE } from '../../../../../../core/models/others.model';

@Component({
  selector: 'cx-banner-carousel',
  templateUrl: './simple-responsive-carousel.component.html',
  styleUrl: './simple-responsive-carousel.component.scss',
  standalone: false,
})
export class SimpleResponsiveCarouselComponent implements OnInit, OnDestroy {
  subscriptions = new Subscription();
  banners: Banner[] = [];
  currentBanner$ = new BehaviorSubject<CmsBannerComponent[]>([]);
  resizeTimeout: any;

  carouselConfig$ = new BehaviorSubject<CustomCarousel>({
    value: [],
  });

  responsiveOptions = [
    {
      breakpoint: mediaConfig.mediaFormats!['widescreen'].width?.toString() + 'px',
      numVisible: 1,
      numScroll: 1,
    },
    {
      breakpoint: mediaConfig.mediaFormats!['desktop'].width?.toString() + 'px',
      numVisible: 1,
      numScroll: 1,
    },
    {
      breakpoint: mediaConfig.mediaFormats!['tablet'].width?.toString() + 'px',
      numVisible: 1,
      numScroll: 1,
    },
    {
      breakpoint: mediaConfig.mediaFormats!['mobile'].width?.toString() + 'px',
      numVisible: 1,
      numScroll: 1,
    },
  ];

  banners$: Observable<Banner[]> = this.componentData.data$.pipe(
    map(component => component.banners?.split(' ')),
    switchMap(bannersIDs => {
      const banners$: Observable<CmsComponent | null>[] | undefined =
        bannersIDs?.map(bannerID => this.cmsService.getComponentData(bannerID)) || [];
      return combineLatest(banners$).pipe(
        map((banners: any[]) => {
          return banners.map(banner => {
            return {
              uid: banner.uid,
              name: banner.name,
              medias: banner.media,
              url: banner.urlLink,
              title: banner.title,
            };
          });
        }),
      );
    }),
  );

  constructor(
    protected componentData: CmsComponentData<CmsBannerCarouselComponent>,
    private cmsService: CmsService,
    protected toolsService: ToolsService,
  ) {}

  ngOnInit(): void {
    this.getContent();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getContent() {
    this.subscriptions.add(
      this.banners$.subscribe(banners => {
        this.banners = banners;
        this.updateMedias();
      }),
    );
  }

  updateMedias() {
    const currentMedias: CmsBannerComponent[] = this.banners.map(banner => ({
      uid: banner.uid,
      name: banner.name,
      title: banner.title,
      urlLink: banner.url,
      media: banner.medias ? this.toolsService.getCurrentMediaFormat(banner.medias) : undefined,
    }));

    this.currentBanner$.next(currentMedias);
  }

  handlerResize() {
    this.updateMedias();
  }

  getCurrentResponsiveConfig() {
    const currentDimension = this.toolsService.getCurrentSize();

    if (currentDimension === DEVICE_SIZE.WIDESCREEN) {
      return this.responsiveOptions[0];
    }

    if (currentDimension === DEVICE_SIZE.DESKTOP) {
      return this.responsiveOptions[1];
    }

    if (currentDimension === DEVICE_SIZE.TABLET) {
      return this.responsiveOptions[2];
    }

    return this.responsiveOptions[3];
  }

  @HostListener('window:resize') onResize() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      this.handlerResize();
    }, 500);
  }
}
